/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * SuperAdmin Sidebar
 * ============================================================================
 *
 * Navigation sidebar with collapsible sections.
 * Features:
 * - Collapsible menu sections with localStorage persistence
 * - Smooth animations and elegant transitions
 * - Search handled by GlobalSearch at the ModuleShell level
 *
 * ============================================================================
 */

import { AppVersion } from '@/components/ui/AppVersion';
import { NavLink, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  LayoutDashboard,
  Building2,
  FileText,
  CreditCard,
  Users,
  Wallet,
  KeyRound,
  LifeBuoy,
  ClipboardList,
  Settings,
  Coins,
  BadgeDollarSign,
  Webhook,
  Tags,
  X,
  Radio,
  Activity,
  Clock,
  Mail,
  Shield,
  ToggleLeft,
  ChevronDown,
  Boxes,
  FileStack,
  RefreshCw,
  DatabaseZap,
  Puzzle,
  ShieldCheck,
  BookOpen,
  Scale,
  ClipboardCheck,
  ArchiveRestore,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores/uiStore';
import { projectConfig } from '@/config/project.config';

import { useTheme } from '@/lib/theme';
import { useDashboardSummary } from '@/hooks/api/useDashboard';

// Import logos as Vite modules
import logoDark from '@/assets/logos/dark/corex-logo-dark.svg';
import logoLight from '@/assets/logos/light/corex-logo-light.svg';

export interface NavItem {
  labelKey: string;
  href: string;
  icon: LucideIcon;
  badgeKey?: 'registrations' | 'withdrawals';
}

export interface NavSection {
  id: string;
  titleKey: string;
  icon?: LucideIcon;
  items: NavItem[];
  defaultCollapsed?: boolean;
}

export const navSectionsConfig: NavSection[] = [
  {
    id: 'overview',
    titleKey: 'core:sidebar.sections.overview',
    icon: LayoutDashboard,
    items: [
      { labelKey: 'core:sidebar.items.dashboard', href: '/superadmin', icon: LayoutDashboard },
    ],
  },
  {
    id: 'organizations',
    titleKey: 'core:sidebar.sections.organizations',
    icon: Building2,
    items: [
      { labelKey: 'core:sidebar.items.allOrganizations', href: '/superadmin/organizations', icon: Building2 },
      { labelKey: 'core:sidebar.items.registrations', href: '/superadmin/registrations', icon: FileText, badgeKey: 'registrations' },
    ],
  },
  {
    id: 'billing',
    titleKey: 'core:sidebar.sections.billing',
    icon: CreditCard,
    items: [
      { labelKey: 'core:sidebar.items.modulePricing', href: '/superadmin/module-pricing', icon: Tags },
      { labelKey: 'core:sidebar.items.invoices', href: '/superadmin/invoices', icon: FileText },
      { labelKey: 'core:sidebar.items.allPayments', href: '/superadmin/payments', icon: CreditCard },
      { labelKey: 'core:sidebar.items.subscriptionLifecycle', href: '/superadmin/subscription-lifecycle', icon: RefreshCw },
      { labelKey: 'platform:addons.nav', href: '/superadmin/addons', icon: Puzzle },
    ],
  },
  {
    id: 'partners',
    titleKey: 'core:sidebar.sections.partners',
    icon: Users,
    items: [
      { labelKey: 'core:sidebar.items.partners', href: '/superadmin/partners', icon: Users },
      { labelKey: 'core:sidebar.items.commissions', href: '/superadmin/commissions', icon: BadgeDollarSign },
      { labelKey: 'core:sidebar.items.withdrawals', href: '/superadmin/withdrawals', icon: Wallet, badgeKey: 'withdrawals' },
      { labelKey: 'core:sidebar.items.inviteCodes', href: '/superadmin/invite-codes', icon: KeyRound },
    ],
  },
  {
    id: 'ledger',
    titleKey: 'core:sidebar.sections.ledger',
    icon: BookOpen,
    defaultCollapsed: true,
    items: [
      { labelKey: 'core:sidebar.items.journalExplorer', href: '/superadmin/journal', icon: BookOpen },
      { labelKey: 'core:sidebar.items.trialBalance', href: '/superadmin/journal/trial-balance', icon: Scale },
      { labelKey: 'core:sidebar.items.ledgerAccounts', href: '/superadmin/journal/accounts', icon: ClipboardCheck },
      { labelKey: 'core:sidebar.items.balanceVerification', href: '/superadmin/journal/verify', icon: ShieldCheck },
    ],
  },
  {
    id: 'uat',
    titleKey: 'core:sidebar.sections.uat',
    icon: ClipboardCheck,
    defaultCollapsed: true,
    items: [
      { labelKey: 'core:sidebar.items.uatReview', href: '/superadmin/uat-review', icon: ClipboardCheck },
    ],
  },
  {
    id: 'system',
    titleKey: 'core:sidebar.sections.system',
    icon: Settings,
    defaultCollapsed: true,
    items: [
      { labelKey: 'core:sidebar.items.roleTemplates', href: '/superadmin/role-templates', icon: Shield },
      { labelKey: 'core:sidebar.items.dataIntegrity', href: '/superadmin/data-integrity', icon: ShieldCheck },
      { labelKey: 'core:sidebar.items.systemHealth', href: '/superadmin/system-health', icon: Activity },
      { labelKey: 'core:sidebar.items.scheduledJobs', href: '/superadmin/jobs', icon: Clock },
      { labelKey: 'core:sidebar.items.supportTools', href: '/superadmin/support', icon: LifeBuoy },
      { labelKey: 'core:sidebar.items.supportAccess', href: '/superadmin/support-access', icon: Shield },
      { labelKey: 'core:sidebar.items.auditLogs', href: '/superadmin/audit-logs', icon: ClipboardList },
      { labelKey: 'core:sidebar.items.webhooks', href: '/superadmin/webhooks', icon: Webhook },
      { labelKey: 'core:sidebar.items.events', href: '/superadmin/events', icon: Radio },
      { labelKey: 'core:sidebar.items.emails', href: '/superadmin/emails', icon: Mail },
      { labelKey: 'core:sidebar.items.security', href: '/superadmin/security', icon: Shield },
      { labelKey: 'core:sidebar.items.featureFlags', href: '/superadmin/feature-flags', icon: ToggleLeft },
      { labelKey: 'core:sidebar.items.moduleManagement', href: '/superadmin/modules', icon: Boxes },
      { labelKey: 'core:sidebar.items.platformSettings', href: '/superadmin/settings', icon: Settings },
      { labelKey: 'core:sidebar.items.currencies', href: '/superadmin/currencies', icon: Coins },
      { labelKey: 'core:sidebar.items.invoiceSettings', href: '/superadmin/invoice-settings', icon: FileText },
      { labelKey: 'core:sidebar.items.templateCatalog', href: '/superadmin/template-catalog', icon: FileStack },
      { labelKey: 'core:sidebar.items.dataMigration', href: '/superadmin/migration', icon: DatabaseZap },
      { labelKey: 'core:sidebar.items.tenantData', href: '/superadmin/tenant-data', icon: ArchiveRestore },
    ],
  },
];

// LocalStorage key for collapsed state
const COLLAPSED_STATE_KEY = 'sidebar-collapsed-sections';

// Get initial collapsed state from localStorage or defaults
function getInitialCollapsedState(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(COLLAPSED_STATE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parse errors
  }
  // Default: collapse sections marked as defaultCollapsed
  const defaults: Record<string, boolean> = {};
  navSectionsConfig.forEach(section => {
    defaults[section.id] = section.defaultCollapsed || false;
  });
  return defaults;
}

/**
 * SuperAdminSidebarContent — nav sections only.
 * No outer <aside> wrapper, no header (logo), no footer (user avatar).
 * Used by ModuleShell which provides its own generic header/footer.
 * Search is handled by GlobalSearch at the ModuleShell level.
 */
export function SuperAdminSidebarContent() {
  const { t } = useTranslation('core');
  const location = useLocation();
  const setBottomNavSections = useUIStore((s) => s.setBottomNavSections);

  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(getInitialCollapsedState);

  const { data: dashboardData } = useDashboardSummary();

  useEffect(() => {
    localStorage.setItem(COLLAPSED_STATE_KEY, JSON.stringify(collapsedSections));
  }, [collapsedSections]);

  const badgeCounts = useMemo(() => {
    const counts: Record<string, number> = { registrations: 0, withdrawals: 0 };
    if (dashboardData?.attentionItems) {
      dashboardData.attentionItems.forEach((item) => {
        if (item.type === 'PENDING_REGISTRATION') counts.registrations = item.count;
        else if (item.type === 'PENDING_WITHDRAWAL') counts.withdrawals = item.count;
      });
    }
    return counts;
  }, [dashboardData]);

  // Push enriched sections into context for MobileBottomNav
  const enrichedForContext = useMemo(() => {
    return navSectionsConfig.map((section) => ({
      ...section,
      items: section.items.map((item) => {
        const count = item.badgeKey ? badgeCounts[item.badgeKey] || 0 : 0;
        return count > 0 ? { ...item, badge: count, badgeVariant: 'danger' as const } : item;
      }),
    }));
  }, [badgeCounts]);

  useEffect(() => {
    setBottomNavSections(enrichedForContext);
  }, [enrichedForContext, setBottomNavSections]);

  const toggleSection = useCallback((sectionId: string) => {
    setCollapsedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  }, []);

  const isRouteInSection = useCallback((section: NavSection) => {
    return section.items.some(item => location.pathname === item.href ||
      (item.href !== '/superadmin' && location.pathname.startsWith(item.href)));
  }, [location.pathname]);

  useEffect(() => {
    navSectionsConfig.forEach(section => {
      if (isRouteInSection(section) && collapsedSections[section.id]) {
        setCollapsedSections(prev => ({ ...prev, [section.id]: false }));
      }
    });
  }, [location.pathname, isRouteInSection, collapsedSections]);

  return (
    <>
      {/* Navigation */}
      <nav className="sidebar-nav">
        {navSectionsConfig.map((section) => {
          const isCollapsed = collapsedSections[section.id];
          const hasActiveItem = isRouteInSection(section);
          const sectionBadgeCount = section.items.reduce((sum, item) =>
            sum + (item.badgeKey ? badgeCounts[item.badgeKey] || 0 : 0), 0);

          return (
            <div key={section.id} className="nav-section">
              <button
                className={cn('nav-label', hasActiveItem && 'has-active')}
                onClick={() => toggleSection(section.id)}
              >
                <span>{t(section.titleKey)}</span>
                <div className="nav-label-right">
                  {isCollapsed && sectionBadgeCount > 0 && (
                    <span className="nav-section-badge">{sectionBadgeCount}</span>
                  )}
                  <ChevronDown
                    size={12}
                    className={cn('nav-chevron', isCollapsed && 'collapsed')}
                  />
                </div>
              </button>
              <div className={cn('nav-items', isCollapsed && 'collapsed')}>
                <div className="nav-items-inner">
                  {section.items.map((item) => (
                    <NavLink
                      key={item.href}
                      to={item.href}
                      end={item.href === '/superadmin'}
                      className={({ isActive }) => cn('nav-item', isActive && 'active')}
                    >
                      <item.icon className="nav-item-icon" />
                      <span>{t(item.labelKey)}</span>
                      {item.badgeKey && badgeCounts[item.badgeKey] > 0 && (
                        <span className="nav-badge">{badgeCounts[item.badgeKey]}</span>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </nav>

      <style>{sidebarStyles}</style>
    </>
  );
}

export function Sidebar() {
  const { t } = useTranslation('core');
  const { isDark } = useTheme();
  const { isSidebarOpen, closeSidebar } = useUIStore();
  const location = useLocation();

  // Collapsed sections state
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(getInitialCollapsedState);

  // Fetch dashboard data for badge counts
  const { data: dashboardData } = useDashboardSummary();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    closeSidebar();
  }, [location.pathname, closeSidebar]);

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem(COLLAPSED_STATE_KEY, JSON.stringify(collapsedSections));
  }, [collapsedSections]);

  // Use imported logo based on theme
  const logo = isDark ? logoDark : logoLight;

  // Calculate badge counts from attention items
  const badgeCounts = useMemo(() => {
    const counts: Record<string, number> = {
      registrations: 0,
      withdrawals: 0,
    };

    if (dashboardData?.attentionItems) {
      dashboardData.attentionItems.forEach((item) => {
        if (item.type === 'PENDING_REGISTRATION') {
          counts.registrations = item.count;
        } else if (item.type === 'PENDING_WITHDRAWAL') {
          counts.withdrawals = item.count;
        }
      });
    }

    return counts;
  }, [dashboardData]);

  // Toggle section collapse
  const toggleSection = useCallback((sectionId: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  }, []);

  // Check if current route is in a section (to auto-expand)
  const isRouteInSection = useCallback((section: NavSection) => {
    return section.items.some(item => location.pathname === item.href ||
      (item.href !== '/superadmin' && location.pathname.startsWith(item.href)));
  }, [location.pathname]);

  // Auto-expand section if current route is in it
  useEffect(() => {
    navSectionsConfig.forEach(section => {
      if (isRouteInSection(section) && collapsedSections[section.id]) {
        setCollapsedSections(prev => ({ ...prev, [section.id]: false }));
      }
    });
  }, [location.pathname, isRouteInSection, collapsedSections]);

  return (
    <>
      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar} />
      )}

      <aside className={cn('sidebar', isSidebarOpen && 'sidebar-open')}>
        {/* Header */}
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <div className="sidebar-logo">
              <img src={logo} alt={projectConfig.name} />
            </div>
            <div>
              <div className="sidebar-title">{projectConfig.name}</div>
              <div className="sidebar-subtitle">{t('sidebar.superAdmin')}</div>
            </div>
          </div>
          <button className="sidebar-close-btn" onClick={closeSidebar}>
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {navSectionsConfig.map((section) => {
            const isCollapsed = collapsedSections[section.id];
            const hasActiveItem = isRouteInSection(section);
            const sectionBadgeCount = section.items.reduce((sum, item) =>
              sum + (item.badgeKey ? badgeCounts[item.badgeKey] || 0 : 0), 0);

            return (
              <div key={section.id} className="nav-section">
                <button
                  className={cn('nav-label', hasActiveItem && 'has-active')}
                  onClick={() => toggleSection(section.id)}
                >
                  <span>{t(section.titleKey)}</span>
                  <div className="nav-label-right">
                    {isCollapsed && sectionBadgeCount > 0 && (
                      <span className="nav-section-badge">{sectionBadgeCount}</span>
                    )}
                    <ChevronDown
                      size={12}
                      className={cn('nav-chevron', isCollapsed && 'collapsed')}
                    />
                  </div>
                </button>
                <div className={cn('nav-items', isCollapsed && 'collapsed')}>
                  <div className="nav-items-inner">
                    {section.items.map((item) => (
                      <NavLink
                        key={item.href}
                        to={item.href}
                        end={item.href === '/superadmin'}
                        className={({ isActive }) =>
                          cn('nav-item', isActive && 'active')
                        }
                      >
                        <item.icon className="nav-item-icon" />
                        <span>{t(item.labelKey)}</span>
                        {item.badgeKey && badgeCounts[item.badgeKey] > 0 && (
                          <span className="nav-badge">{badgeCounts[item.badgeKey]}</span>
                        )}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        {/* Footer — legal/copyright only */}
        <div className="sidebar-footer">
          <div className="sidebar-legal">
            <span className="sidebar-legal-copyright">&copy; 2026 CoreX-Suite</span>
            <span className="sidebar-legal-sep">·</span>
            <NavLink to="/privacy" className="sidebar-legal-link">
              {t('core:sidebar.privacy', 'Confidentialité')}
            </NavLink>
            <span className="sidebar-legal-sep">·</span>
            <NavLink to="/terms" className="sidebar-legal-link">
              {t('core:sidebar.terms', 'Conditions')}
            </NavLink>
            <span className="sidebar-legal-sep">·</span>
            <span className="sidebar-legal-version"><AppVersion /></span>
          </div>
        </div>

        <style>{sidebarStyles}</style>
      </aside>
    </>
  );
}

const sidebarStyles = `
  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    width: 220px;
    height: 100vh;
    background: var(--color-bg-secondary);
    border-right: 1px solid var(--color-border-subtle);
    display: flex;
    flex-direction: column;
    z-index: 100;
  }

  .sidebar-header {
    padding: 12px 16px;
    border-bottom: 1px solid var(--color-border-subtle);
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
  }

  .sidebar-close-btn {
    display: none;
    padding: 6px;
    border: none;
    background: transparent;
    color: var(--color-text-secondary);
    cursor: pointer;
    border-radius: 6px;
    transition: all 0.15s ease;
  }

  .sidebar-close-btn:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  .sidebar-overlay {
    display: none;
  }

  .sidebar-brand {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .sidebar-logo {
    width: 32px;
    height: 32px;
    background: var(--color-brand);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
  }

  .sidebar-logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .sidebar-title {
    font-family: var(--font-heading);
    font-size: 13px;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .sidebar-subtitle {
    font-size: 10px;
    color: var(--color-text-secondary);
  }

  .sidebar-nav {
    flex: 1;
    padding: 4px 8px;
    overflow-y: auto;
  }

  .nav-section {
    margin-bottom: 4px;
  }

  .nav-label {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 9px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--color-text-muted);
    padding: 8px 12px 4px;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: color 0.15s ease;
  }

  .nav-label:hover {
    color: var(--color-text-secondary);
  }

  .nav-label.has-active {
    color: var(--color-brand);
  }

  .nav-label-right {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .nav-section-badge {
    background: var(--color-error, #DC2626);
    color: white;
    font-size: 9px;
    font-weight: 700;
    padding: 1px 5px;
    border-radius: 99px;
    min-width: 16px;
    text-align: center;
  }

  .nav-chevron {
    transition: transform 0.2s ease;
  }

  .nav-chevron.collapsed {
    transform: rotate(-90deg);
  }

  .nav-items {
    display: grid;
    grid-template-rows: 1fr;
    transition: grid-template-rows 0.25s ease-out;
  }

  .nav-items.collapsed {
    grid-template-rows: 0fr;
  }

  .nav-items-inner {
    overflow: hidden;
    min-height: 0;
  }

  .nav-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 6px;
    color: var(--color-text-secondary);
    font-size: 12px;
    cursor: pointer;
    margin-bottom: 2px;
    text-decoration: none;
    transition: all 0.15s ease;
  }

  .nav-item:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  .nav-item.active {
    background: rgba(139, 0, 0, 0.15);
    color: var(--color-brand);
  }

  .nav-item-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .nav-badge {
    margin-left: auto;
    background: var(--color-error, #DC2626);
    color: white;
    font-size: 9px;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 99px;
  }

  .sidebar-footer {
    border-top: 1px solid var(--color-border-subtle);
    flex-shrink: 0;
  }

  .sidebar-legal {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0;
    padding: 8px 12px;
    flex-shrink: 0;
  }

  .sidebar-legal-copyright {
    color: var(--color-text-muted);
    font-size: 0.65rem;
    flex-shrink: 0;
  }

  .sidebar-legal-link {
    color: var(--color-text-muted);
    font-size: 9px;
    text-decoration: none;
    letter-spacing: 0.3px;
    transition: color 0.15s ease;
  }

  .sidebar-legal-link:hover { color: var(--color-text-secondary); }

  .sidebar-legal-sep {
    color: var(--color-border-subtle);
    margin: 0 5px;
    font-size: 9px;
  }

  .sidebar-legal-version {
    color: var(--color-text-muted);
    font-size: 9px;
    margin-left: auto;
  }

  @media (max-width: 768px) {
    .sidebar {
      transform: translateX(-100%);
      transition: transform 0.3s ease;
    }

    .sidebar.sidebar-open {
      transform: translateX(0);
    }

    .sidebar-close-btn {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .sidebar-overlay {
      display: block;
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 99;
      animation: fadeIn 0.2s ease;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
  }
`;

